import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { InternalTaskBoard } from './InternalTaskBoard';
import { InternalTeamList } from './InternalTeamList';
import { WebhookEventsViewer } from './WebhookEventsViewer';
import {
  Monitor,
  ListTodo,
  CheckCircle,
  Users,
  ShieldAlert,
  Activity,
  Server,
  Webhook,
} from 'lucide-react';

export function ITManagerDashboard() {
  const { data: stats } = useQuery({
    queryKey: ['it-manager-stats'],
    queryFn: async () => {
      const dept = await supabase
        .from('internal_departments')
        .select('id')
        .eq('name', 'IT')
        .maybeSingle();

      const deptId = dept.data?.id;

      const [teams, allTasks, openTasks, doneTasks, tickets, openTickets] = await Promise.all([
        deptId
          ? supabase
              .from('internal_teams')
              .select('*', { count: 'exact', head: true })
              .eq('department_id', deptId)
          : Promise.resolve({ count: 0 } as any),
        supabase.from('internal_tasks').select('*', { count: 'exact', head: true }),
        supabase
          .from('internal_tasks')
          .select('*', { count: 'exact', head: true })
          .in('status', ['todo', 'in_progress']),
        supabase
          .from('internal_tasks')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'done'),
        supabase.from('support_tickets').select('*', { count: 'exact', head: true }),
        supabase
          .from('support_tickets')
          .select('*', { count: 'exact', head: true })
          .in('status', ['open', 'in_progress']),
      ]);

      return {
        totalTeams: teams.count || 0,
        totalTasks: allTasks.count || 0,
        openTasks: openTasks.count || 0,
        doneTasks: doneTasks.count || 0,
        totalTickets: tickets.count || 0,
        openTickets: openTickets.count || 0,
      };
    },
  });

  const completion =
    stats && stats.totalTasks > 0
      ? Math.round((stats.doneTasks / stats.totalTasks) * 100)
      : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Monitor className="h-6 w-6 text-primary" />
            IT Manager Dashboard
          </h1>
          <p className="text-muted-foreground">
            Oversee IT operations, infrastructure, support tickets, and your team.
          </p>
        </div>
        <Badge variant="outline" className="gap-1">
          <Activity className="h-3 w-3" /> Systems Operational
        </Badge>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Users className="h-8 w-8 text-blue-500" />
            <div>
              <p className="text-2xl font-bold">{stats?.totalTeams ?? 0}</p>
              <p className="text-xs text-muted-foreground">IT Teams</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <ListTodo className="h-8 w-8 text-orange-500" />
            <div>
              <p className="text-2xl font-bold">{stats?.openTasks ?? 0}</p>
              <p className="text-xs text-muted-foreground">Open Tasks</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle className="h-8 w-8 text-green-500" />
            <div>
              <p className="text-2xl font-bold">{stats?.doneTasks ?? 0}</p>
              <p className="text-xs text-muted-foreground">Completed</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Activity className="h-8 w-8 text-primary" />
            <div>
              <p className="text-2xl font-bold">{completion}%</p>
              <p className="text-xs text-muted-foreground">Completion</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <ShieldAlert className="h-8 w-8 text-red-500" />
            <div>
              <p className="text-2xl font-bold">{stats?.openTickets ?? 0}</p>
              <p className="text-xs text-muted-foreground">Open Tickets</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Server className="h-8 w-8 text-purple-500" />
            <div>
              <p className="text-2xl font-bold">{stats?.totalTickets ?? 0}</p>
              <p className="text-xs text-muted-foreground">Total Tickets</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="tasks" className="space-y-4">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="tasks">
            <ListTodo className="h-4 w-4 mr-2" /> All Tasks
          </TabsTrigger>
          <TabsTrigger value="my-tasks">
            <CheckCircle className="h-4 w-4 mr-2" /> My Tasks
          </TabsTrigger>
          <TabsTrigger value="teams">
            <Users className="h-4 w-4 mr-2" /> IT Teams
          </TabsTrigger>
          <TabsTrigger value="webhooks">
            <Webhook className="h-4 w-4 mr-2" /> System Events
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tasks" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Department Task Board</CardTitle>
            </CardHeader>
            <CardContent>
              <InternalTaskBoard />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="my-tasks" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Tasks Assigned to Me</CardTitle>
            </CardHeader>
            <CardContent>
              <InternalTaskBoard assignedToMe />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="teams" className="space-y-4">
          <InternalTeamList departmentName="IT" canManage />
        </TabsContent>

        <TabsContent value="webhooks" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Webhook & System Events</CardTitle>
            </CardHeader>
            <CardContent>
              <WebhookEventsViewer />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
